import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, connect } from 'react-redux';
import { isEmpty, reduce, reject, forEach, isBoolean, isUndefined, omit, isEqual, union, mapValues, keys } from 'lodash-es';
import axios from '../configs/axios';
import createStore from './create-store';

const makeCancelSource = () => axios.CancelToken.source();
const isRequestCancellationError = error => axios.isCancel(error);

let parallelCancelSource;
const cancelParallelRequests = () => {
  if (parallelCancelSource) parallelCancelSource.cancel();
};

const doRequest = ({
  request,
  requestURL,
  requestMethod,
  advancedParams = {},
}, successCallback, failCallback) => {
  if (requestMethod === 'post') {
    axios.post(requestURL, { request }, advancedParams)
      .then(successCallback)
      .catch((error) => {
        if (!isRequestCancellationError(error)) {
          failCallback(error);
        }
      });
  } else {
    axios.get(requestURL, { params: { request }, ...advancedParams })
      .then(successCallback)
      .catch((error) => {
        if (!isRequestCancellationError(error)) {
          failCallback(error);
        }
      });
  }
};

const isAssetPending = (state, assetName) => {
  const asset = state.assets && state.assets[assetName];
  if (isUndefined(asset)) return true;
  const { settings } = asset;
  if (isEmpty(settings)) return false;
  return isBoolean(settings.pending) ? settings.pending : true;
};

const isAllAssetsPending = (state) => {
  const { structure } = state;
  if (isEmpty(structure.assets)) return true;
  return reduce(structure.assets, (memo, assetData, assetName) => (
    memo && isAssetPending(state, assetName)
  ), true);
};

const loadedSettings = settings => ({
  ...(settings || {}),
  pending: false,
  loaded: true,
  error: false,
});

const assetsForInitialData = (data, request) => {
  const assets = {};
  forEach(data.assets, (dataAsset, assetName) => {
    assets[assetName] = {
      ...dataAsset,
      settings: {
        error: false,
        pending: false,
        loaded: !request.assets[assetName],
      },
    };
  });
  return assets;
};

const assetsForRequest = (oldAssets, request) => {
  const assets = {};
  forEach(request.assets, (assetRequest, assetName) => {
    const asset = (oldAssets || {})[assetName] || {};
    assets[assetName] = {
      ...asset,
      settings: {
        ...(asset.settings || {}),
        pending: true,
        loaded: (asset.settings || {}).loaded || false,
        error: false,
      },
      filters: {
        ...(asset.filters || {}),
        ...(assetRequest || {}).filters,
      },
    };
  });
  return { ...oldAssets, ...assets };
};

const assetsForFailedRequest = (oldAssets, request) => {
  const error = true;
  const assets = {};
  forEach(request.assets, (assetRequest, assetName) => {
    const asset = (oldAssets || {})[assetName] || {};
    assets[assetName] = {
      ...asset,
      settings: {
        ...(asset.settings || {}),
        pending: false,
        loaded: (asset.settings || {}).loaded || false,
        error,
      },
      filters: {
        ...(asset.filters || {}),
        ...(assetRequest || {}).filters,
      },
    };
  });
  return { ...oldAssets, ...assets };
};

const stateForMissingDataRequest = (oldState, request, data) => {
  const error = false;
  const assets = assetsForRequest(assetsForInitialData(data, request), request);

  const state = {
    ...oldState,
    ...(data || {}),
  };
  if (isEmpty(assets)) {
    state.filters = request.filters;
  } else {
    state.assets = assets;
  }
  state.settings = {
    ...(state.settings || {}),
    error,
    pending: isAllAssetsPending(state),
  };
  return state;
};

const stateForFailedRequest = (oldState, request) => {
  const error = true;
  const assets = assetsForFailedRequest(oldState.assets, request);

  const state = { ...oldState };
  if (isEmpty(assets)) {
    state.settings = {
      ...(state.settings || {}), error, pending: false,
    };
    state.filters = request.filters;
  } else {
    state.settings = {
      ...(state.settings || {}),
      error: error && isAllAssetsPending(state),
      pending: false,
    };
    state.assets = assets;
  }
  return state;
};

const stateForRequest = (oldState, request) => {
  const assets = assetsForRequest(oldState.assets, request);

  const state = { ...oldState };
  if (isEmpty(assets)) {
    state.filters = request.filters;
    state.settings = {
      ...(state.settings || {}), error: false, pending: true,
    };
  } else {
    state.assets = assets;
    state.settings = {
      ...(state.settings || {}),
      error: false,
      pending: isAllAssetsPending(state),
    };
  }
  return state;
};

const initState = (state, data) => {
  const { structure } = state;
  const assets = {};
  if (Array.isArray(structure.assets)) {
    forEach(structure.assets, (assetName) => {
      assets[assetName] = data[assetName];
    });
    return {
      ...state,
      ...assets,
      settings: loadedSettings(state.settings),
      filters: data.filters,
    };
  }

  forEach(structure.assets, (assetStructure, assetName) => {
    const asset = data.assets[assetName] || {};
    assets[assetName] = {
      ...asset,
      settings: loadedSettings(asset.settings),
    };
  });
  return { ...state, assets, settings: loadedSettings(state.settings) };
};

const mergeState = (state, data) => {
  const assets = {};

  forEach(data.assets, (assetData, assetName) => {
    const asset = state.assets[assetName] || {};
    assets[assetName] = {
      ...asset,
      ...assetData,
      settings: loadedSettings(asset.settings),
    };
  });
  let newState = {
    ...state,
    settings: loadedSettings(state.settings),
  };

  if (!isEmpty(assets)) {
    newState.assets = { ...(state.assets || {}), ...assets };
  } else {
    newState = { ...newState, ...data };
  }
  return newState;
};

const mergeCompositeSubAsset = (state, assetName, subAssetName, data) => {
  const { assets } = state;
  const asset = assets[assetName];
  const subAsset = asset[subAssetName];
  return {
    ...state,
    settings: loadedSettings(state.settings),
    assets: {
      ...assets,
      [assetName]: {
        ...asset,
        settings: loadedSettings(asset.settings),
        [subAssetName]: mapValues(subAsset, (val, key) => ({ ...val, ...data[key] })),
      },
    },
  };
};

export const initDataSuccess = data => ({
  type: 'INIT_DATA_SUCCESS',
  data,
});

export const fetchDataRequest = (request, page, appIds) => ({
  type: 'FETCH_DATA_REQUEST',
  request,
  page,
  appIds,
});

export const missingDataRequest = (data, request) => ({
  type: 'MISSING_DATA_REQUEST',
  data,
  request,
});

export const dataRequest = request => ({
  type: 'DATA_REQUEST',
  request,
});

export const fetchDataSuccess = data => ({
  type: 'FETCH_DATA_SUCCESS',
  data,
});

export const fetchCompositeDataSuccess = (asset, subAsset, data) => ({
  type: 'FETCH_COMPOSITE_DATA_SUCCESS',
  data,
  asset,
  subAsset,
});

export const fetchDataFailed = request => ({
  type: 'FETCH_DATA_FAILED',
  request,
});

export const setUrl = url => ({
  type: 'SET_URL',
  url,
});

const hasDataForAsset = (node, asset) => {
  const hasAssets = isUndefined(node.assets);
  if (hasAssets && isUndefined(node[asset])) return false;
  if (!hasAssets && isEmpty(node.assets[asset])) return false;
  return true;
};

const missingAssetRequest = (key, options, missingAssets) => {
  const request = {};
  const assets = {};
  if (options.assetsFilters) {
    missingAssets.forEach((asset) => {
      const filters = options.assetsFilters[asset];
      assets[asset] = isEmpty(filters) ? null : { filters };
    });
  } else {
    request.assets = missingAssets;
  }
  if (!isEmpty(options.filters)) {
    request.filters = { ...options.filters };
  }
  if (!isEmpty(assets)) {
    request.assets = { ...assets };
  }
  return isEmpty(request) ? {} : { [key]: request };
};

const checkNodeAssets = (key, options, node) => {
  if (!isEqual(options.filters, (node && node.filters))) {
    return missingAssetRequest(key, options, options.assets);
  }
  const missingAssets = isEmpty(node)
    ? options.assets
    : reject(options.assets, asset => (hasDataForAsset(node, asset)));
  return isEmpty(missingAssets) ? {} : missingAssetRequest(key, options, missingAssets);
};

const checkNode = (key, options, node) => {
  if (options && options.assets) {
    return checkNodeAssets(key, options, node);
  }
  return {};
};

const missingDataZeroLevel = (data, structure) => {
  const request = { assets: [] };
  if (structure.filters && !isEqual(data.filters, structure.filters)) {
    request.assets = structure.assets;
    request.filters = structure.filters;
  } else {
    forEach(structure.assets, (asset) => {
      if (isUndefined(data[asset])) {
        request.assets = [...request.assets, asset];
      }
    });
    if (data.filters) { request.filters = data.filters; }
  }
  return request;
};

const missingData = (data, structure) => {
  if (Array.isArray(structure.assets)) {
    return missingDataZeroLevel(data, structure);
  }

  return {
    assets: reduce(structure.assets, (memo, value, key) => {
      const assetData = isEmpty(data.assets) ? {} : data.assets[key];
      const missing = checkNode(key, value, assetData);
      return { ...memo, ...missing };
    }, {}),
  };
};

const executeSingleOrParallelRequests = ({
  request,
  requestURL,
  requestMethod,
  advancedParams,
}, dispatch) => {
  const { parallelLoading = false, requestCancellation = false } = advancedParams;
  let requestAdvancedParams;

  if (requestCancellation) {
    cancelParallelRequests();
    parallelCancelSource = makeCancelSource();
    requestAdvancedParams = { cancelToken: parallelCancelSource.token };
  }

  if (parallelLoading) {
    forEach(request.assets, (assetRequest, assetName) => {
      const requestByAsset = { assets: { [assetName]: assetRequest } };
      doRequest(
        {
          request: requestByAsset,
          requestURL,
          requestMethod,
          advancedParams: requestAdvancedParams,
        },
        result => dispatch(fetchDataSuccess(result.data)),
        () => dispatch(fetchDataFailed(requestByAsset)),
      );
    });
  } else {
    doRequest(
      { request, requestURL, requestMethod, advancedParams: requestAdvancedParams },
      result => dispatch(fetchDataSuccess(result.data)),
      () => dispatch(fetchDataFailed(request)),
    );
  }
};

const requestMissingData = (data, request, advancedParams) => (dispatch, getState) => {
  const { url: requestURL, httpMethod: requestMethod } = getState();

  executeSingleOrParallelRequests({
    request,
    requestURL,
    requestMethod,
    advancedParams,
  }, dispatch);

  dispatch(missingDataRequest(data, request));
};

export const initData = (data, advancedParams = {}) => (dispatch, getState) => {
  const { structure } = getState();
  const request = missingData(data, structure);

  if (isEmpty(request.assets)) {
    dispatch(initDataSuccess(data));
  } else {
    dispatch(requestMissingData(data, request, advancedParams));
  }
};

export const requestSingleAsset = (
  assetName,
  filters,
  childAssets,
  advancedParams = {}) => (dispatch, getState) => {
  const { url: requestURL, httpMethod: requestMethod, assets } = getState();

  const prevFilters = assets[assetName].filters;
  const params = { filters: { ...prevFilters, ...filters } };
  if (!isEmpty(childAssets)) params.assets = childAssets;
  const request = { assets: { [assetName]: params } };

  executeSingleOrParallelRequests({ request, requestURL, requestMethod, advancedParams }, dispatch);

  return dispatch(dataRequest(request));
};

export const requestMultiplesAssets = (assetsNames, filters, advancedParams = {}) => (
  (dispatch, getState) => {
    const { url: requestURL, httpMethod: requestMethod, assets } = getState();

    const mergedAssets = reduce(assetsNames, (memo, assetName) => {
      const prevFilters = assets[assetName].filters;
      const params = {
        filters: { ...prevFilters, ...filters },
        assets: keys(omit(assets[assetName], 'filters', 'settings')),
      };
      return {
        ...memo,
        [assetName]: params,
      };
    }, {});
    const request = { assets: mergedAssets };

    executeSingleOrParallelRequests({
      request,
      requestURL,
      requestMethod,
      advancedParams,
    }, dispatch);

    return dispatch(dataRequest(request));
  });

export const requestNextPageForAsset = (assetName, filters, childAssets) => (
  (dispatch, getState) => {
    const { url: requestURL, assets, httpMethod: requestMethod } = getState();

    const prevFilters = assets[assetName].filters;
    const params = {
      assets: childAssets,
      filters: { ...prevFilters, ...filters },
    };
    const request = { assets: { [assetName]: params } };

    const mergeResult = (result) => {
      const newAssets = reduce(childAssets, (memo, asset) => {
        const prevData = assets[assetName][asset];
        const newData = result.assets[assetName][asset] || (Array.isArray(prevData) ? [] : {});

        if (Array.isArray(prevData) && Array.isArray(newData)) {
          return { ...memo, [asset]: [...prevData, ...newData] };
        }

        return {
          ...memo,
          [asset]: reduce(prevData, (acc, prevDataValue, prevDataKey) => ({
            ...acc,
            [prevDataKey]: [...prevDataValue, ...(newData[prevDataKey] || [])],
          }), {}),
        };
      }, {});

      return {
        assets: {
          [assetName]: newAssets,
        },
      };
    };

    doRequest(
      { request, requestURL, requestMethod },
      result => dispatch(fetchDataSuccess(mergeResult(result.data))),
      () => dispatch(fetchDataFailed(request)),
    );

    return dispatch(dataRequest(request));
  });

export const requestCompositeSubAsset = (assetName, subAsset, filterName, filterValue) => (
  (dispatch, getState) => {
    const { url: requestURL, httpMethod: requestMethod, assets } = getState();

    const asset = assets[assetName];
    const prevFilters = asset.filters;
    const params = {
      assets: [subAsset],
      filters: {
        ...prevFilters,
        [filterName]: filterValue,
      },
    };
    const request = { assets: { [assetName]: params } };
    const newFilterValue = Array.isArray(prevFilters[filterName])
      ? union(filterValue, prevFilters[filterName])
      : filterValue;
    const mergedParams = {
      assets: asset.assets,
      filters: {
        ...prevFilters,
        [filterName]: newFilterValue,
      },
    };
    const mergedRequest = { assets: { [assetName]: mergedParams } };

    doRequest(
      { request, requestURL, requestMethod },
      (result) => {
        const assetData = result.data.assets[assetName][subAsset];
        dispatch(fetchCompositeDataSuccess(assetName, subAsset, assetData));
      },
      () => dispatch(fetchDataFailed(mergedRequest)),
    );

    return dispatch(dataRequest(mergedRequest));
  });

// This code is no longer used
export const requestByFilters = newFilters => (dispatch, getState) => {
  const { url: requestURL, httpMethod: requestMethod, filters } = getState();
  const request = { filters: { ...filters, ...newFilters } };
  doRequest(
    { request, requestURL, requestMethod },
    result => dispatch(fetchDataSuccess(result.data)),
    () => dispatch(fetchDataFailed(request)),
  );
  return dispatch(dataRequest(request));
};

const renderComponent = (rootNode, store, component, mapStateToProps) => {
  document.addEventListener('DOMContentLoaded', () => {
    const root = createRoot(rootNode);
    root.render(
      <Provider store={store}>
        {React.createElement(connect(mapStateToProps)(component))}
      </Provider>,
    );
  });
};

// This code is no longer used
export const renderZeroLevelComponent = (rootNode, store, component) => {
  const mapStateToProps = state => ({
    assets: omit(state, 'settings', 'filters', 'structure', 'url', 'httpMethod'),
    settings: state.settings,
  });
  renderComponent(rootNode, store, component, mapStateToProps);
};

export const renderOneLevelComponent = (rootNode, store, component) => {
  const mapStateToProps = state => ({
    assets: state.assets,
    settings: state.settings,
  });
  renderComponent(rootNode, store, component, mapStateToProps);
};

export const rootReducer = (state = {}, action) => {
  switch (action.type) {
    case 'INIT_DATA_SUCCESS':
      return initState(state, action.data);
    case 'MISSING_DATA_REQUEST':
      return stateForMissingDataRequest(state, action.request, action.data);
    case 'DATA_REQUEST':
      return stateForRequest(state, action.request);
    case 'FETCH_DATA_SUCCESS':
      return mergeState(state, action.data);
    case 'FETCH_COMPOSITE_DATA_SUCCESS':
      return mergeCompositeSubAsset(state, action.asset, action.subAsset, action.data);
    case 'FETCH_DATA_FAILED':
      return stateForFailedRequest(state, action.request);
    case 'SET_URL':
      return { ...state, url: action.url };
    default:
      return state;
  }
};

export const configureStore = config => (
  createStore(
    rootReducer,
    { httpMethod: 'get', ...config, settings: { loaded: false } },
  )
);
